import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch, useLocation } from 'react-router-dom';

import { RouteWhenAuthenticated } from 'common/components';
import NotFound from 'core/NotFound';
import BrandRoutes from 'core/routes/BrandRoutes';
import EnterpriseRoutes from 'core/routes/EnterpriseRoutes';
import IdentityVerificationRoutes from 'modules/compliance/kyc-process/IdentityVerificationRoutes';
import DeveloperPreviews from 'modules/developer-previews';
import { Experience, useIsNewExperienceEnabled } from 'modules/experience';
import { FrontPage } from 'modules/front-page';
import { InviteToken } from 'modules/invites';
import { Login, EmailLogin } from 'modules/login';
import OnboardingPage from 'modules/onboarding-page/OnboardingPage';
import PhotoShootRoutes from 'modules/photo-shoot/PhotoShootRoutes/PhotoShootRoutes';
import { SupportLayer } from 'modules/support';
import VenueDataCollection from 'modules/venue-data-collection';

import MerchantRoutes from './MerchantRoutes';
import VenueRoutes from './VenueRoutes';
import { CreateAdCampaignDeeplink } from '../../modules/campaigns/ad-campaign/components/create-campaign-deeplink/CreateAdCampaignDeeplink';
import MerchantSignUp from '../../modules/merchant-sign-up';
import ExistingMerchantSignUp from '../../modules/merchant-sign-up/ExistingMerchantSignUp';
import InvitedUserSignUp from '../../modules/merchant-sign-up/InvitedUserSignUp';
import MerchantSelfServeSignUp from '../../modules/merchant-sign-up/MerchantSelfServeSignUp';
import SignUpRedirectWrapper from '../../modules/merchant-sign-up/SignUpRedirectWrapper';
import { TaxReporting } from '../../modules/payout-reports/TaxReporting';
import { RouteWhenNotAuthenticated } from '../../modules/self-serve-sign-up';
import SelfServeSignUp from '../../modules/self-serve-sign-up';
import SignContract from '../../modules/self-serve-sign-up/SignContract/SignContract';

type Props = {
  forceIDVerification: boolean;
  setSkipIDVerification: (skipIDVerification: boolean) => void;
};

type ExperienceRouteParams = {
  feature: string;
  entityId: string;
  rest: string;
  enterpriseSlug: string;
};

const ExperienceRoutesChanges: Record<string, string> = {
  'menu-editor': 'shared-menu',
  'venues/groups': 'venue-groups',
  'venues/historical-venue-availability': 'venues/historical',
  reports: 'payout-reports',
  'reporting/payout': 'payout-reports',
};

const ExperienceRenderComponent = ({
  match: {
    params: { feature, entityId, rest },
  },
}: RouteComponentProps<ExperienceRouteParams>) => {
  if (!['enterprise', 'brand', 'venue', 'merchant'].includes(feature)) {
    return;
  }
  if (feature === 'enterprise' && rest?.startsWith('brand')) {
    return;
  }
  const finalRestOfThePath = rest ? ExperienceRoutesChanges[rest] || rest : '';
  return <Redirect to={`/experience/${feature}/${entityId}/${finalRestOfThePath}`} />;
};

const RootRoutes = ({ forceIDVerification, setSkipIDVerification }: Props) => {
  // Remove trailing slash from URL
  const { pathname, search } = useLocation();
  const { isNewExperienceEnabled } = useIsNewExperienceEnabled();
  // .slice(1) is important, because the root route always has a trailing slash
  if (pathname.slice(1).endsWith('/')) {
    return <Redirect to={`${pathname.slice(0, -1)}${search}`} />;
  }

  return (
    <Switch>
      <Route exact path="/login" component={Login} />
      <Route exact path="/email_login" component={EmailLogin} />
      <Route path="/photo-shoot/:venueId" component={PhotoShootRoutes} />
      <Route path="/merchant-sign-up" component={SignUpRedirectWrapper} />
      <Route path="/existing-merchant-sign-up" component={ExistingMerchantSignUp} />
      <Route path="/draft-merchant-sign-up" component={MerchantSignUp} />
      <Route path="/invited-user-sign-up" component={InvitedUserSignUp} />
      <Route path="/merchant-self-serve-signup" component={MerchantSelfServeSignUp} />
      <Route path="/tax-reporting" component={TaxReporting} />
      <RouteWhenAuthenticated path="/venue-data-collection/:merchantId" component={VenueDataCollection} />
      <RouteWhenAuthenticated
        path="/identity-verification"
        render={props => <IdentityVerificationRoutes {...props} setSkipIDVerification={setSkipIDVerification} />}
      />
      {forceIDVerification && <Redirect to="/identity-verification" />}
      <RouteWhenAuthenticated
        exact
        path="/"
        component={() => {
          return isNewExperienceEnabled ? <Redirect to={'/experience/search'} /> : <FrontPage />;
        }}
      />
      <RouteWhenAuthenticated exact path="/invite/:token" component={InviteToken} />
      <RouteWhenNotAuthenticated path="/partner-with-wolt" component={SelfServeSignUp} />
      <RouteWhenAuthenticated exact path="/ad-campaign-creation" component={CreateAdCampaignDeeplink} />
      <RouteWhenAuthenticated path="/onboarding-contract" component={SignContract} />
      <RouteWhenAuthenticated path="/support" component={SupportLayer} />
      <RouteWhenAuthenticated
        path="/:feature(onboarding-page)"
        render={props => <OnboardingPage {...props} setSkipIDVerification={setSkipIDVerification} />}
      />
      {process.env.APP_ENV !== 'production' && (
        <RouteWhenAuthenticated exact path="/developer-previews" component={DeveloperPreviews} />
      )}
      <RouteWhenAuthenticated path="/experience" component={Experience} />
      {isNewExperienceEnabled
        ? [
            <RouteWhenAuthenticated
              path="/enterprise/:enterpriseSlug/:feature(brand)/:entityId/:rest*"
              render={ExperienceRenderComponent}
              key="experienceBrandRedirect"
            />,
            <RouteWhenAuthenticated
              path="/:feature(enterprise)/:entityId/:rest*"
              render={ExperienceRenderComponent}
              key="experienceEnterpriseRedirect"
            />,
            <RouteWhenAuthenticated
              path="/:feature(merchant)/:entityId/:rest*"
              render={ExperienceRenderComponent}
              key="experienceMerchantRedirect"
            />,
            <RouteWhenAuthenticated
              path="/:feature(venue)/:entityId/:rest*"
              render={ExperienceRenderComponent}
              key="experienceVenueRedirect"
            />,
          ]
        : [
            <RouteWhenAuthenticated
              path="/:feature(enterprise)/:slug/brand/:brandId"
              component={BrandRoutes}
              key="brandRoutes"
            />,
            <RouteWhenAuthenticated
              path="/:feature(enterprise)/:slug"
              component={EnterpriseRoutes}
              key="enterpriseRoutes"
            />,
            <RouteWhenAuthenticated path="/:feature(merchant)/:slug" component={MerchantRoutes} key="merchantRoutes" />,
            <RouteWhenAuthenticated path="/:feature(venue)/:slug" component={VenueRoutes} key="venueRoutes" />,
          ]}
      <Route component={NotFound} />
    </Switch>
  );
};

export default RootRoutes;
